import React, { useContext, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Pagesection, Tabs, TabsPanel } from '@ktag/styleguide/dist/react';

import MunicipalityList from 'lib/MunicipalityList/MunicipalityList';
import PartyBarList from 'lib/PartyBarList/PartyBarList';
import ApiContext from 'api/ApiContext';
import { CountingCirclesRecord, PollDetails } from 'api/api.types';
import { MuniTab } from 'lib/MapSwitch/MapSwitch';
import useMapApi from 'lib/Map/useMapApi';
import MunicipalityView from 'lib/MunicipalityView/MunicipalityView';
import { PartyBarData } from 'lib/PartyBar/partybar.types';

const renderAccordeon = (
  listsWithDetails: PartyBarData[],
  currentElectionYear: number,
  previousElectionResults?: PollDetails,
  prevYear?: number,
) => {
  return (
    { lists, results }: CountingCirclesRecord,
    prevRecord?: CountingCirclesRecord,
  ) => {
    const idsOfUsedPrevLists: number[] = [];
    const partyBarData: PartyBarData[] = lists.map((list) => {
      const listWithDetails = listsWithDetails.find(
        (li) => li.listId === list.list_group_id,
      );

      // find list from previous election via partyId since only this is consistent
      const partyIdOfCurrentList = listWithDetails?.partyId;
      const listIdOfPrevList = previousElectionResults?.list_groups?.find(
        (listGroup) => listGroup.party_id === partyIdOfCurrentList,
      )?.id;

      const prevList = prevRecord?.lists?.find(
        (li) => li.list_group_id === listIdOfPrevList,
      );

      if (prevList) {
        idsOfUsedPrevLists.push(prevList.list_group_id);
      }

      return {
        title: listWithDetails?.title,
        shortTitle: listWithDetails?.shortTitle,
        votesRelative: list?.results?.party_votes_percentage,
        votesAbsolute: list?.results?.party_votes,
        votesAbsolutePrev: prevList?.results?.party_votes,
        votesRelativePrev: prevList?.results?.party_votes_percentage,
        listId: listWithDetails?.listId,
        listNumber: listWithDetails?.listNumber,
        competedInPrevElection: listWithDetails?.competedInPrevElection,
      };
    });

    return (
      <PartyBarList
        isFinal
        lists={partyBarData}
        type="votes"
        currentElectionYear={currentElectionYear}
        eligibleVoters={results?.eligible_voters}
        participation={results?.participation}
        prevElectionYear={prevYear}
      />
    );
  };
};

interface Props {
  lists: PartyBarData[];
  pollId: string;
  currentElectionYear: number;
  prevElectionYear: number;
  isFinal: boolean;
  previousElectionId: string;
  previousElectionResults: PollDetails;
  prevYear?: number;
}

const ProportionalProportionsGrw = ({
  currentElectionYear,
  isFinal,
  lists,
  pollId,
  prevElectionYear,
  previousElectionId,
  previousElectionResults,
  prevYear,
}: Props) => {
  const { getCountingCircles, getAggregatingDomains } = useContext(ApiContext);
  const { t } = useTranslation('wap');

  const [selectedMuniTab, setSelectedMuniTab] = useState<MuniTab>('map');

  const municipalityQuery = useQuery(['counting_circles', pollId], () =>
    getCountingCircles(pollId),
  );
  const districtsQuery = useQuery(['aggregating_domains', pollId], () =>
    getAggregatingDomains(pollId),
  );
  const districtQueryPrevElection = useQuery(
    ['aggregating_domains', previousElectionId],
    () => getAggregatingDomains(previousElectionId),
  );

  const {
    map,
    hoveredElement: hoveredMapElement,
    selectedElement: selectedMapElement,
    isLoading: isLoadingMap,
    setSelectedElement: setSelectedMapElement,
  } = useMapApi({
    year: currentElectionYear,
    records: municipalityQuery.data?.data?.records,
    type: 'proportional',
    selectedMuniTab,
  });

  return (
    <Pagesection>
      <Tabs
        noBezel
        additionalClassNames="proportionalelectiondetails__proportions-tabs"
        ariaLabel={t('proportional_tablist_proportions_level_2')}
      >
        <TabsPanel label={t('canton')} id="canton">
          <PartyBarList
            lists={lists}
            currentElectionYear={currentElectionYear}
            prevElectionYear={prevElectionYear}
            type="votes"
            isFinal={isFinal}
            useLegendVotersString
            isCantonalTab
            enforceDecimals
          />
        </TabsPanel>
        <TabsPanel label={t('proportional_districts')} id="districts">
          <MunicipalityList
            hideSearchbar
            checksum={municipalityQuery.data?.meta?.data_checksum_sha256}
            districts={districtsQuery.data?.data?.records}
            prevDistricts={districtQueryPrevElection.data?.data?.records}
            render={renderAccordeon(
              lists,
              currentElectionYear,
              previousElectionResults,
              prevYear,
            )}
          />
        </TabsPanel>
        <TabsPanel
          label={t('municipalities')}
          id="municipalities"
          additionalClassNames="proportionalelectiondetails__proportions-munis"
        >
          <MunicipalityView
            hoveredMapElement={hoveredMapElement}
            selectedMapElement={selectedMapElement}
            isLoadingMap={isLoadingMap}
            map={map}
            query={municipalityQuery}
            render={renderAccordeon(lists, currentElectionYear)}
            setSelectedMapElement={setSelectedMapElement}
            selectedMuniTab={selectedMuniTab}
            setSelectedMuniTab={setSelectedMuniTab}
          />
        </TabsPanel>
      </Tabs>
    </Pagesection>
  );
};

export default ProportionalProportionsGrw;
